import { useCallback, useEffect, useState } from 'react';
import {
    Grid,
    Paper,
    Typography,
    Button,
    Tabs,
    Tab,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import b64toBlob from 'b64-to-blob';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';

import { useWidgetContext } from '../../../hooks/widgetContext';
import { fetchTeamScheduleNeedsSummary, exportTeamScheduleNeedsSummary, MenuProps } from '../../Widgets/WidgetService';
import TeamScheduleNeedsSummaryModel, { IScheduledMemberNeed, IProofDocument } from '../../../Models/Widget/TeamScheduleNeedsSummary.model';
import { parentUrl } from '../../../utils/constants';
import HealthPlanMultiSelect from '../../Shared/HealthPlanMultiSelect';
import '../../Widgets/Widgets.css';

interface TeamScheduleNeedsProps {
    teamId?: string;
}

const TeamScheduleNeedsSummaryView = ({ teamId }: TeamScheduleNeedsProps) => {
    const [isMembersLoading, setIsMembersLoading] = useState<boolean>(false);
    const [summary, setSummary] = useState<TeamScheduleNeedsSummaryModel>(new TeamScheduleNeedsSummaryModel());
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<number>(0);

    // Proof Document Modal State
    const [proofModalOpen, setProofModalOpen] = useState<boolean>(false);
    const [selectedMemberProof, setSelectedMemberProof] = useState<{ memberName: string; proofs: IProofDocument[] } | null>(null);

    const {
        teamMemberSelectedPlans,
        setTeamMemberSelectedPlans,
        teamMemberSelectedTeam,
        teamShowInActiveTeams,
        teamMemberFromDate,
        setTeamMemberFromDate,
        teamMemberToDate,
        setTeamMemberToDate,
    } = useWidgetContext();

    const targetTeamId = teamId || teamMemberSelectedTeam;

    const loadNeedsSummary = useCallback(async () => {
        setIsMembersLoading(false);
        const data = await fetchTeamScheduleNeedsSummary(
            teamMemberSelectedPlans.join(','),
            targetTeamId,
            Boolean(teamMemberFromDate) ? new Date(teamMemberFromDate).toISOString() : '',
            Boolean(teamMemberToDate) ? new Date(teamMemberToDate).toISOString() : '',
            teamShowInActiveTeams
        );
        setSummary(data || new TeamScheduleNeedsSummaryModel());
        setIsMembersLoading(true);
    }, [teamMemberSelectedPlans, targetTeamId, teamMemberFromDate, teamMemberToDate, teamShowInActiveTeams]);

    useEffect(() => {
        if ((teamMemberFromDate != null && teamMemberToDate != null) || (teamMemberFromDate == null && teamMemberToDate == null)) {
            loadNeedsSummary();
        }
    }, [loadNeedsSummary, teamMemberFromDate, teamMemberToDate]);

    const exportDetail = async () => {
        setDownLoading(true);
        const data = await exportTeamScheduleNeedsSummary(
            teamMemberSelectedPlans.join(','),
            targetTeamId,
            Boolean(teamMemberFromDate) ? new Date(teamMemberFromDate).toISOString() : '',
            Boolean(teamMemberToDate) ? new Date(teamMemberToDate).toISOString() : '',
            teamShowInActiveTeams
        );
        const b64Str = typeof data === 'string' ? data : data?.base64String;
        let blob = b64toBlob(b64Str, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1;
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `TeamSchedule_MemberNeedsProof - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    const handleOpenProofModal = (row: IScheduledMemberNeed) => {
        const mName = row.MemberName || row.memberName || `${row.FirstName || row.firstName || ''} ${row.LastName || row.lastName || ''}`.trim();
        const proofs = row.ProofDocuments || row.proofDocuments || [];
        setSelectedMemberProof({
            memberName: mName,
            proofs: proofs,
        });
        setProofModalOpen(true);
    };

    const getActiveList = (): IScheduledMemberNeed[] => {
        const s = summary as any;
        const allWithNeeds = s.AllScheduledMembersWithNeeds || s.allScheduledMembersWithNeeds || [];
        const openTcNoVisit = s.ScheduledMembersOpenTcNoLicensedVisit || s.scheduledMembersOpenTcNoLicensedVisit || [];
        const inTcNotDischarged = s.ScheduledMembersInTcNotDischarged || s.scheduledMembersInTcNotDischarged || [];
        const expiredHedis = s.ScheduledMembersExpiredOrNotMetHedis || s.scheduledMembersExpiredOrNotMetHedis || [];
        const missingConsent = s.ScheduledMembersMissingConsentOrCarePlan || s.scheduledMembersMissingConsentOrCarePlan || [];
        const canceled = s.ScheduledMembersCanceledNeedingReschedule || s.scheduledMembersCanceledNeedingReschedule || [];

        switch (activeTab) {
            case 0:
                return allWithNeeds;
            case 1:
                return openTcNoVisit;
            case 2:
                return inTcNotDischarged;
            case 3:
                return expiredHedis;
            case 4:
                return missingConsent;
            case 5:
                return canceled;
            default:
                return allWithNeeds;
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 50,
            sortable: false,
            renderCell: (params) => (
                <Typography variant="body2">{params.api.getSortedRowIds().indexOf(params.id) + 1}</Typography>
            ),
        },
        {
            field: 'MemberName',
            headerName: 'Scheduled Member',
            flex: 1.2,
            renderCell: (params) => {
                const row = params.row as IScheduledMemberNeed;
                const mName = row.MemberName || row.memberName || `${row.FirstName || row.firstName || ''} ${row.LastName || row.lastName || ''}`.trim();
                const mId = row.MemberId || row.memberId;
                return (
                    <Typography
                        variant="body2"
                        color="primary"
                        sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                        onClick={() => window.open(`${parentUrl}provider/members-list/member-chart/${mId}/member-profile`, '_blank')}
                    >
                        {mName}
                    </Typography>
                );
            },
        },
        {
            field: 'AhcccsId',
            headerName: 'AHCCCS ID',
            flex: 0.8,
            renderCell: (params) => {
                const row = params.row as IScheduledMemberNeed;
                return <Typography variant="body2">{row.AhcccsId || row.ahcccsId || '-'}</Typography>;
            },
        },
        {
            field: 'ScheduledAppointmentDate',
            headerName: 'Scheduled Date (Next 7 Days)',
            flex: 1.2,
            renderCell: (params) => {
                const row = params.row as IScheduledMemberNeed;
                const val = row.ScheduledAppointmentDate || row.scheduledAppointmentDate;
                const isValidDate = val && new Date(val).getFullYear() > 2000;
                return (
                    <Typography variant="body2">
                        {isValidDate ? new Date(val).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </Typography>
                );
            },
        },
        {
            field: 'ScheduledProvider',
            headerName: 'Scheduled Provider',
            flex: 1,
            renderCell: (params) => {
                const row = params.row as IScheduledMemberNeed;
                return <Typography variant="body2">{row.ScheduledProvider || row.scheduledProvider || '-'}</Typography>;
            },
        },
        {
            field: 'HealthPlan',
            headerName: 'Health Plan',
            flex: 1,
            renderCell: (params) => {
                const row = params.row as IScheduledMemberNeed;
                return <Typography variant="body2">{row.HealthPlan || row.healthPlan || '-'}</Typography>;
            },
        },
        {
            field: 'NeedSummary',
            headerName: 'Clinical Need Summary',
            flex: 2,
            renderCell: (params) => {
                const row = params.row as IScheduledMemberNeed;
                const summaryText = row.NeedSummary || row.needSummary || '-';
                return (
                    <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {summaryText}
                    </Typography>
                );
            },
        },
        {
            field: 'ProofDocuments',
            headerName: 'Citations Used',
            flex: 1,
            renderCell: (params) => {
                const row = params.row as IScheduledMemberNeed;
                const proofs = row.ProofDocuments || row.proofDocuments || [];
                return (
                    <Tooltip title="View Record Citations">
                        <Chip
                            icon={<VerifiedIcon />}
                            label={`${proofs.length} Citation(s)`}
                            color="success"
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenProofModal(row)}
                            sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                        />
                    </Tooltip>
                );
            },
        },
    ];

    const s = summary as any;
    const refDateStr = s.ReferenceDate || s.referenceDate;
    const totalScheduled = s.TotalScheduledMembersNext7Days ?? s.totalScheduledMembersNext7Days ?? 0;

    const countAll = (s.AllScheduledMembersWithNeeds || s.allScheduledMembersWithNeeds || []).length;
    const countOpenTc = (s.ScheduledMembersOpenTcNoLicensedVisit || s.scheduledMembersOpenTcNoLicensedVisit || []).length || (s.CountOpenTcNoLicensedVisit ?? s.countOpenTcNoLicensedVisit ?? 0);
    const countInTc = (s.ScheduledMembersInTcNotDischarged || s.scheduledMembersInTcNotDischarged || []).length || (s.CountInTcNotDischarged ?? s.countInTcNotDischarged ?? 0);
    const countHedis = (s.ScheduledMembersExpiredOrNotMetHedis || s.scheduledMembersExpiredOrNotMetHedis || []).length || (s.CountExpiredOrNotMetHedis ?? s.countExpiredOrNotMetHedis ?? 0);
    const countConsent = (s.ScheduledMembersMissingConsentOrCarePlan || s.scheduledMembersMissingConsentOrCarePlan || []).length || (s.CountMissingConsentOrCarePlan ?? s.countMissingConsentOrCarePlan ?? 0);
    const countCanceled = (s.ScheduledMembersCanceledNeedingReschedule || s.scheduledMembersCanceledNeedingReschedule || []).length || (s.CountCanceledNeedingReschedule ?? s.countCanceledNeedingReschedule ?? 0);

    const tabCategories = [
        { label: 'All Flagged Members', count: countAll },
        { label: 'Open TC - Missing Licensed Visit', count: countOpenTc },
        { label: 'In TC - Pending Discharge', count: countInTc },
        { label: 'Expired / Not Met HEDIS', count: countHedis },
        { label: 'Missing Consent / Signed Care Plan', count: countConsent },
        { label: 'Canceled - Needs Reschedule', count: countCanceled },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Paper elevation={3} sx={{ p: 2, mt: 1 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="h6" color="primary" fontWeight="bold">
                                Team Schedule & Clinical Needs (7-Day Scope)
                            </Typography>
                            <Chip
                                label={`Reference Date: ${new Date(refDateStr || new Date()).toLocaleDateString('en-US')}`}
                                variant="outlined"
                                color="default"
                                size="small"
                                sx={{ fontWeight: 'bold' }}
                            />
                            {totalScheduled > 0 && (
                                <Chip
                                    label={`Scheduled Members: ${totalScheduled}`}
                                    color="primary"
                                    size="small"
                                    sx={{ fontWeight: 'bold' }}
                                />
                            )}
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }} textAlign="right">
                        <Button onClick={exportDetail} variant="contained" color="success">
                            {downLoading ? 'Exporting...' : 'Export Excel with Citations'}
                        </Button>
                    </Grid>
                </Grid>

                <Grid container spacing={2} mt={1} alignItems="center">
                    <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                        <HealthPlanMultiSelect
                            value={teamMemberSelectedPlans}
                            onChange={setTeamMemberSelectedPlans}
                            menuProps={MenuProps}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                        <DesktopDatePicker
                            label="From Date"
                            value={Boolean(teamMemberFromDate) ? teamMemberFromDate : null}
                            minDate={new Date('2017-01-01')}
                            onChange={(newValue) => setTeamMemberFromDate(newValue)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                        <DesktopDatePicker
                            label="To Date"
                            value={Boolean(teamMemberToDate) ? teamMemberToDate : null}
                            minDate={new Date('2017-01-01')}
                            onChange={(newValue) => setTeamMemberToDate(newValue)}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                    <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)} variant="scrollable" scrollButtons="auto">
                        {tabCategories.map((cat, idx) => (
                            <Tab
                                key={idx}
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <span>{cat.label}</span>
                                        <Chip label={cat.count} size="small" color={cat.count > 0 ? 'error' : 'default'} />
                                    </Box>
                                }
                            />
                        ))}
                    </Tabs>
                </Box>

                <Box mt={2} sx={{ height: '55vh', width: '100%', position: 'relative' }}>
                    {!isMembersLoading ? (
                        <Box className="loader-center">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <DataGrid
                            columns={columns}
                            rows={getActiveList()}
                            getRowId={(row: any) => `${row.MemberId || row.memberId || Math.random()}-${row.AppointmentId || row.appointmentId || Math.random()}`}
                            paginationMode="client"
                            pageSizeOptions={[10, 25, 50, 100]}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25, page: 0 } },
                            }}
                            density="comfortable"
                        />
                    )}
                </Box>

                {/* Proof Document Modal Dialog */}
                <Dialog open={proofModalOpen} onClose={() => setProofModalOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VerifiedIcon color="success" />
                        Document Citations ({selectedMemberProof?.memberName})
                    </DialogTitle>
                    <DialogContent dividers>
                        {selectedMemberProof?.proofs.length === 0 ? (
                            <Typography color="textSecondary">No citations attached.</Typography>
                        ) : (
                            selectedMemberProof?.proofs.map((proof: any, i) => {
                                const sType = proof.SourceRecordType || proof.sourceRecordType;
                                const title = proof.Title || proof.title;
                                const status = proof.Status || proof.status;
                                const dDate = proof.DocumentDate || proof.documentDate;
                                const rId = proof.RecordId || proof.recordId;
                                const excerpt = proof.ProofExcerpt || proof.proofExcerpt;
                                return (
                                    <Paper key={i} elevation={2} sx={{ p: 2, mb: 2, backgroundColor: '#f9fbe7', borderLeft: '4px solid #4caf50' }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                [{sType}] {title}
                                            </Typography>
                                            <Chip label={status || 'Verified'} size="small" color="primary" variant="outlined" />
                                        </Box>
                                        {dDate && (
                                            <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                                                Record Date: {new Date(dDate).toLocaleDateString()} | Record ID: {rId}
                                            </Typography>
                                        )}
                                        <Typography variant="body2" sx={{ fontStyle: 'italic', backgroundColor: '#ffffff', p: 1.5, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                            "{excerpt}"
                                        </Typography>
                                    </Paper>
                                );
                            })
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setProofModalOpen(false)} color="primary" variant="contained">
                            Close Citations
                        </Button>
                    </DialogActions>
                </Dialog>
            </Paper>
        </LocalizationProvider>
    );
};

export default TeamScheduleNeedsSummaryView;
